/** @jsxImportSource @emotion/react */
import React, { useState } from 'react';
import { getErrorMessage } from '../../utils/toast';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { css } from '@emotion/react';
import commonStyles from '../../styles/commonStyles';
import {
  Card,
  Table,
  Tag,
  Button,
  Space,
  Typography,
  Progress,
  Select,
  Spin,
  Alert,
  Badge,
  Popconfirm,
  message,
  Modal,
  Input,
} from 'antd';
import { CheckCircleOutlined, ClockCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import {
  useConfirmEventMutation,
  useGetEventsQueueQuery,
  useRejectEventMutation,
} from '../../api/plantingEventsApi';
import { EventQueueItem, EventQueueStatus, EventQueueType } from '../../types/plantingEvents';
import { useSave } from '../../contexts/WorldContext';
import { CompleteEventModal } from './CompleteEventModal';
import { EventDetailsModal } from './EventDetailsModal';
import { hasAction } from '../../types/permissions';
import { EventKindTag } from '../../utils/eventKindHelpers';
import { useTranslation } from 'react-i18next';
import { formatDate } from '../../i18n';
import { eventStatusLabel } from './utils/eventFormatters';

const { Title, Text } = Typography;
const { Option } = Select;

const styles = {
  container: css({ padding: '24px' }),
  header: css({ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }),
  title: css({ margin: 0 }),
  statsRow: css({ display: 'flex', justifyContent: 'space-between' }),
  statsProgress: css({ display: 'flex', justifyContent: 'space-around' }),
  badgePadding: css({ padding: '0 20px' }),
  progressContainer: css({ minWidth: '100px' }),
  spinnerContainer: css({ padding: '40px' }),
};

/**
 * Unified компонент очереди событий (CN-0008 refactoring)
 * Единая страница "Задачи" с быстрым переключателем assignee
 */
export const EventsQueueUnified: React.FC = () => {
  const { t } = useTranslation('events');
  const { saveId } = useParams<{ saveId: string }>();
  const save = useSave();
  const farmId = save.farmId;
  const farmIdValue = farmId || '';

  // State
  const [statusFilter, setStatusFilter] = useState<EventQueueStatus | undefined>('planned');
  // Harvests demoted to a task after a cursor rollback come back as decision_state
  // 'proposed' (status stays 'completed') — surface them via this filter so they can
  // be confirmed (re-apply) or rejected (discard) regardless of status.
  const [decisionFilter, setDecisionFilter] = useState<'proposed' | undefined>(undefined);
  const [typeFilter, setTypeFilter] = useState<EventQueueType | undefined>(undefined);
  const [assigneeFilter, setAssigneeFilter] = useState<'all' | 'me' | 'unassigned'>('all');
  const [selectedEvent, setSelectedEvent] = useState<EventQueueItem | null>(null);
  const [viewEventId, setViewEventId] = useState<string | null>(null);
  const [rejectModalEventId, setRejectModalEventId] = useState<string | null>(null);
  const [rejectNote, setRejectNote] = useState('');
  
  const navigate = useNavigate();

  const user = useSelector((state: any) => state.auth.user);

  // Права
  const canCompleteEvents =
    user?.role === 'superuser' ||
    user?.role === 'agronomist' ||
    hasAction(user, 'events', 'events.complete', farmIdValue) ||
    hasAction(user, 'events', 'events.update', farmIdValue);

  // API
  const {
    data: queueData,
    isLoading,
    error,
    refetch,
  } = useGetEventsQueueQuery(
    {
      saveId: saveId || '',
      // When filtering by "awaiting decision" we drop the status filter so proposed
      // items surface regardless of their status (rolled-back harvests are 'completed').
      status: decisionFilter ? undefined : statusFilter,
      type: typeFilter,
      assignee: assigneeFilter,
      decisionState: decisionFilter,
    },
    { skip: !saveId }
  );
  const [confirmEvent, { isLoading: confirmingEvent }] = useConfirmEventMutation();
  const [rejectEvent, { isLoading: rejectingEvent }] = useRejectEventMutation();

  // Группировка batch событий
  const groupedEvents = React.useMemo(() => {
    if (!queueData?.events) return [];

    const childEventsByParent = new Map<string, EventQueueItem[]>();
    const parentEvents: EventQueueItem[] = [];
    const standaloneEvents: EventQueueItem[] = [];

    queueData.events.forEach((event) => {
      if (event.parentEventId) {
        const siblings = childEventsByParent.get(event.parentEventId) || [];
        siblings.push(event);
        childEventsByParent.set(event.parentEventId, siblings);
      } else if (event.isBatch) {
        parentEvents.push(event);
      } else {
        standaloneEvents.push(event);
      }
    });

    // Создаём синтетические родительские события из дочерних
    childEventsByParent.forEach((children, parentId) => {
      if (children.length > 0 && !parentEvents.find((p) => p.id === parentId)) {
        const firstChild = children[0];
        const completedCount = children.filter((c) => c.status === 'completed').length;

        const syntheticParent: EventQueueItem = {
          ...firstChild,
          id: parentId,
          parentEventId: null,
          isBatch: true,
          batchTotal: children.length,
          batchCompleted: completedCount,
        };

        parentEvents.push(syntheticParent);
      }
    });

    return [...parentEvents, ...standaloneEvents];
  }, [queueData?.events]);

  const getStatusTag = (status: EventQueueStatus) => {
    const statusConfig: Record<
      EventQueueStatus,
      { color: string; icon: React.ReactNode }
    > = {
      planned: { color: 'default', icon: <ClockCircleOutlined /> },
      pending: { color: 'orange', icon: <ClockCircleOutlined /> },
      in_progress: { color: 'blue', icon: <ClockCircleOutlined /> },
      completed: { color: 'green', icon: <CheckCircleOutlined /> },
      cancelled: { color: 'red', icon: <CloseCircleOutlined /> },
    };

    const config = statusConfig[status] || statusConfig.planned;
    return (
      <Tag color={config.color} icon={config.icon}>
        {eventStatusLabel(statusConfig[status] ? status : 'planned')}
      </Tag>
    );
  };

  const getDecisionTag = (decisionState?: string) => {
    const state = decisionState || 'confirmed';
    if (state === 'proposed') {
      return <Tag color="gold">{t('decision.proposed')}</Tag>;
    }
    if (state === 'rejected') {
      return <Tag color="red">{t('decision.rejected')}</Tag>;
    }
    return <Tag color="green">{t('decision.confirmed')}</Tag>;
  };

  const getSourceTag = (sourceType?: string, sourceConfidence?: number | null) => {
    const knownSources = ['user', 'worker', 'device', 'external_api', 'simulation'];
    const source = sourceType || 'user';
    const label = knownSources.includes(source) ? t(`sources.${source}`) : source;
    const confidence = sourceConfidence !== null && sourceConfidence !== undefined
      ? ` (${Math.round(sourceConfidence * 100)}%)`
      : '';
    return <Tag>{`${label}${confidence}`}</Tag>;
  };

  const handleConfirm = async (eventId: string) => {
    try {
      await confirmEvent({ saveId: saveId || '', eventId }).unwrap();
      message.success(t('queue.confirmed'));
      refetch();
    } catch (err: any) {
      message.error(err?.data?.message ? getErrorMessage(err) : t('queue.confirmFailed'));
    }
  };

  const handleReject = async (eventId: string, note?: string) => {
    try {
      await rejectEvent({ saveId: saveId || '', eventId, payload: { note } }).unwrap();
      message.success(t('queue.rejected'));
      refetch();
    } catch (err: any) {
      message.error(err?.data?.message ? getErrorMessage(err) : t('queue.rejectFailed'));
    }
  };

  const openRejectModal = (eventId: string) => {
    setRejectModalEventId(eventId);
    setRejectNote('');
  };

  const submitRejectModal = async () => {
    if (!rejectModalEventId) return;
    await handleReject(rejectModalEventId, rejectNote || undefined);
    setRejectModalEventId(null);
    setRejectNote('');
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: t('queue.columns.cropTemplate'),
      key: 'crop',
      render: (record: EventQueueItem) => {
        if (record.isBatch) {
          return (
            <div>
              <Text strong type="success">
                {record.templateName || t('details.batchActivity')}
              </Text>
              <br />
              <Text type="secondary" css={commonStyles.smallText}>
                {record.cropName || '—'}
              </Text>
            </div>
          );
        }
        return (
          <div>
            <Link to={`/saves/${saveId}/plantings/${record.plantingId}`}>
              <Text strong>{record.cropName || '—'}</Text>
            </Link>
            {record.cropLatinName && (
              <>
                <br />
                <Text type="secondary" css={commonStyles.smallText}>
                  {record.cropLatinName}
                </Text>
              </>
            )}
          </div>
        );
      },
    },
    {
      title: t('common:labels.type'),
      dataIndex: 'type',
      key: 'type',
      render: (type: string, record: EventQueueItem) => (
        <div>
          <Space direction="vertical" size="small">
            <Space size="small">
              <Tag>{type}</Tag>
              <EventKindTag kind={record.kind} showIcon={true} />
            </Space>
            {record.isBatch && <Tag color="purple">{t('queue.batchTag')}</Tag>}
          </Space>
        </div>
      ),
    },
    {
      title: t('batchTable.location'),
      key: 'location',
      render: (record: EventQueueItem) => {
        if (record.isBatch) {
          return (
            <Text type="secondary" css={commonStyles.smallText}>
              {t('details.batchActivity')}
            </Text>
          );
        }

        if (record.bedName) {
          return (
            <div css={commonStyles.smallText}>
              <Text strong>{record.bedName}</Text>
              {record.plotName && (
                <>
                  <br />
                  <Text type="secondary">{record.plotName}</Text>
                </>
              )}
            </div>
          );
        }

        if (record.plotName) {
          return (
            <div css={commonStyles.smallText}>
              <Text strong>{record.plotName}</Text>
              {record.fieldName && (
                <>
                  <br />
                  <Text type="secondary">{record.fieldName}</Text>
                </>
              )}
            </div>
          );
        }

        if (record.fieldName) {
          return <Text strong css={commonStyles.smallText}>{record.fieldName}</Text>;
        }

        return <Text type="secondary" css={commonStyles.smallText}>—</Text>;
      },
    },
    {
      title: t('details.progress'),
      key: 'progress',
      render: (record: EventQueueItem) => {
        if (!record.isBatch) {
          return <Text type="secondary">—</Text>;
        }
        const progress =
          record.batchTotal && record.batchTotal > 0
            ? Math.round(((record.batchCompleted || 0) / record.batchTotal) * 100)
            : 0;
        return (
          <div css={styles.progressContainer}>
            <Progress
              percent={progress}
              size="small"
              status={record.status === 'completed' ? 'success' : 'active'}
            />
            <Text type="secondary" css={commonStyles.tinyText}>
              {record.batchCompleted || 0} / {record.batchTotal || 0}
            </Text>
          </div>
        );
      },
    },
    {
      title: t('queue.columns.taskDetails'),
      key: 'properties',
      render: (record: EventQueueItem) => {
        if (!record.properties || record.properties.length === 0) {
          return <Text type="secondary">—</Text>;
        }
        return (
          <div>
            {record.properties.slice(0, 3).map((attr, idx) => {
              const value =
                attr.valueText ?? attr.valueNumber ?? (attr.valueBoolean ? t('common:actions.yes') : t('common:actions.no'));
              return (
                <div key={idx} css={commonStyles.smallText}>
                  <Text type="secondary">{attr.propertyName}:</Text> <Text strong>{value}</Text>
                </div>
              );
            })}
            {record.properties.length > 3 && (
              <Text type="secondary" css={commonStyles.tinyText}>
                {t('queue.more', { count: record.properties.length - 3 })}
              </Text>
            )}
          </div>
        );
      },
    },
    {
      title: t('common:labels.status'),
      dataIndex: 'status',
      key: 'status',
      render: (status: EventQueueStatus) => getStatusTag(status),
    },
    {
      title: t('queue.columns.review'),
      key: 'decision',
      render: (record: EventQueueItem) => (
        <Space direction="vertical" size={2}>
          {getDecisionTag(record.decisionState)}
          {getSourceTag(record.sourceType, record.sourceConfidence)}
          {record.decisionNote && (
            <Text type="secondary" css={commonStyles.tinyText}>
              {record.decisionNote.length > 48
                ? `${record.decisionNote.slice(0, 48)}...`
                : record.decisionNote}
            </Text>
          )}
        </Space>
      ),
    },
    {
      title: t('details.assigned'),
      key: 'assignedTo',
      render: (record: EventQueueItem) =>
        record.assignedTo?.name ? (
          <Text css={commonStyles.smallText}>{record.assignedTo.name}</Text>
        ) : (
          <Text type="secondary" css={commonStyles.smallText}>
            {t('details.unassigned')}
          </Text>
        ),
    },
    {
      title: t('details.plannedDate'),
      dataIndex: 'plannedDate',
      key: 'plannedDate',
      render: (date: string) => {
        if (!date || date === '1970-01-01' || new Date(date).getFullYear() < 2000) {
          return <Text type="secondary" css={commonStyles.smallText}>—</Text>;
        }
        return <Text css={commonStyles.smallText}>{formatDate(date, 'short')}</Text>;
      },
    },
    {
      title: t('common:labels.actions'),
      key: 'actions',
      render: (record: EventQueueItem) => {
        return (
          <Space direction="vertical" size="small">
            <Button size="small" onClick={() => setViewEventId(record.id)}>
              {t('queue.viewDetails')}
            </Button>
            {record.isBatch ? (
              <Button
                type="primary"
                size="small"
                onClick={() => navigate(`/saves/${saveId}/events/${record.id}`)}
              >
                {t('common:actions.details')}
              </Button>
            ) : (
              <>
                {(record.status === 'planned' ||
                  record.status === 'pending' ||
                  record.status === 'in_progress') &&
                  record.decisionState !== 'proposed' &&
                  record.decisionState !== 'rejected' &&
                  canCompleteEvents && (
                    <Button type="primary" size="small" onClick={() => setSelectedEvent(record)}>
                      {t('actions.complete')}
                    </Button>
                  )}
                {record.decisionState === 'proposed' && canCompleteEvents && (
                  <>
                    <Popconfirm
                      title={t('queue.confirmTitle')}
                      description={t('queue.confirmDescription')}
                      onConfirm={() => handleConfirm(record.id)}
                      okText={t('queue.confirm')}
                      cancelText={t('common:actions.cancel')}
                    >
                      <Button
                        type="primary"
                        size="small"
                        loading={confirmingEvent}
                        icon={<CheckCircleOutlined />}
                      >
                        {t('queue.confirm')}
                      </Button>
                    </Popconfirm>
                    <Button
                      danger
                      size="small"
                      loading={rejectingEvent}
                      icon={<CloseCircleOutlined />}
                      onClick={() => openRejectModal(record.id)}
                    >
                      {t('queue.reject')}
                    </Button>
                  </>
                )}
              </>
            )}
            {record.plantingId && (
              <Link to={`/saves/${saveId}/plantings/${record.plantingId}`}>
                <Button size="small">{t('queue.toPlanting')}</Button>
              </Link>
            )}
          </Space>
        );
      },
    },
  ];

  if (!farmIdValue) {
    return (
      <Card>
        <Alert message={t('queue.noFarmId')} type="error" showIcon />
      </Card>
    );
  }

  return (
    <div css={styles.container}>
      <Card>
        <Space direction="vertical" css={commonStyles.fullWidth} size="large">
          {/* Заголовок */}
          <div css={styles.header}>
            <Title level={3} css={styles.title}>
              {t('queue.title')}
            </Title>
            <Button onClick={() => refetch()}>{t('common:actions.refresh')}</Button>
          </div>

          {/* Статистика */}
          {queueData && queueData.byStatus && (
            <Card size="small">
              <Space direction="vertical" css={commonStyles.fullWidth}>
                <div css={styles.statsRow}>
                  <Text strong>{t('queue.completionProgress')}</Text>
                  <Text>
                    {queueData.byStatus.completed} / {queueData.total}
                  </Text>
                </div>
                <Progress
                  percent={
                    queueData.total > 0
                      ? Math.round((queueData.byStatus.completed / queueData.total) * 100)
                      : 0
                  }
                  status="active"
                />
                <div css={styles.statsProgress}>
                  <Badge count={queueData.total} showZero css={css({ backgroundColor: '#1890ff' })}>
                    <div css={styles.badgePadding}>{t('queue.total')}</div>
                  </Badge>
                  <Badge
                    count={queueData.byStatus.planned}
                    showZero
                    css={css({ backgroundColor: '#d9d9d9' })}
                  >
                    <div css={styles.badgePadding}>{t('status.planned')}</div>
                  </Badge>
                  <Badge
                    count={queueData.byStatus.in_progress}
                    showZero
                    css={css({ backgroundColor: '#1890ff' })}
                  >
                    <div css={styles.badgePadding}>{t('queue.inProgress')}</div>
                  </Badge>
                  <Badge
                    count={queueData.byStatus.completed}
                    showZero
                    css={css({ backgroundColor: '#52c41a' })}
                  >
                    <div css={styles.badgePadding}>{t('status.completed')}</div>
                  </Badge>
                </div>
              </Space>
            </Card>
          )}

          {/* Фильтры */}
          <Space wrap>
            <Select
              css={css({ width: 200 })}
              placeholder={t('queue.filters.assignment')}
              value={assigneeFilter}
              onChange={setAssigneeFilter}
            >
              <Option value="all">{t('queue.filters.allTasks')}</Option>
              <Option value="me">{t('queue.filters.assignedToMe')}</Option>
              <Option value="unassigned">{t('queue.filters.unassigned')}</Option>
            </Select>

            <Select
              css={css({ width: 200 })}
              placeholder={t('queue.filters.status')}
              value={statusFilter}
              onChange={setStatusFilter}
              disabled={!!decisionFilter}
              allowClear
            >
              <Option value="planned">{t('status.planned')}</Option>
              <Option value="pending">{t('status.pending')}</Option>
              <Option value="in_progress">{t('status.in_progress')}</Option>
              <Option value="completed">{t('status.completed')}</Option>
              <Option value="cancelled">{t('status.cancelled')}</Option>
            </Select>

            <Select
              css={css({ width: 220 })}
              placeholder={t('queue.filters.decision')}
              value={decisionFilter}
              onChange={(v) => setDecisionFilter(v)}
              allowClear
            >
              <Option value="proposed">{t('queue.filters.needsConfirmation')}</Option>
            </Select>

            <Select
              css={css({ width: 200 })}
              placeholder={t('queue.filters.type')}
              value={typeFilter}
              onChange={setTypeFilter}
              allowClear
            >
              <Option value="measurement">{t('queueTypes.measurement')}</Option>
              <Option value="inspection">{t('queueTypes.inspection')}</Option>
              <Option value="pruning">{t('queueTypes.pruning')}</Option>
              <Option value="watering">{t('queueTypes.watering')}</Option>
              <Option value="fertilizing">{t('queueTypes.fertilizing')}</Option>
              <Option value="pest_control">{t('queueTypes.pest_control')}</Option>
              <Option value="weeding">{t('queueTypes.weeding')}</Option>
              <Option value="general">{t('queueTypes.general')}</Option>
            </Select>
          </Space>

          {/* Таблица */}
          {isLoading ? (
            <div css={[commonStyles.textCenter, styles.spinnerContainer]}>
              <Spin size="large" />
            </div>
          ) : error ? (
            <Alert message={t('batch.loadErrorTitle')} description={t('queue.loadFailed')} type="error" showIcon />
          ) : (
            <Table
              dataSource={groupedEvents}
              columns={columns}
              rowKey="id"
              pagination={{ pageSize: 20 }}
              locale={{ emptyText: t('queue.empty') }}
            />
          )}
        </Space>
      </Card>

      {/* Модалка завершения события */}
      {selectedEvent && (
        <CompleteEventModal
          open={!!selectedEvent}
          event={selectedEvent}
          farmId={farmId || ''}
          onClose={() => setSelectedEvent(null)}
          onSuccess={() => {
            refetch();
            setSelectedEvent(null);
          }}
        />
      )}

      {/* Модалка просмотра деталей события */}
      <EventDetailsModal
        eventId={viewEventId}
        farmId={farmId || ''}
        open={!!viewEventId}
        onClose={() => setViewEventId(null)}
        canEdit={canCompleteEvents}
      />

      <Modal
        title={t('queue.rejectTitle')}
        open={!!rejectModalEventId}
        onCancel={() => setRejectModalEventId(null)}
        onOk={submitRejectModal}
        okText={t('queue.reject')}
        okButtonProps={{ danger: true, loading: rejectingEvent }}
        cancelText={t('common:actions.cancel')}
      >
        <Space direction="vertical" css={commonStyles.fullWidth}>
          <Text type="secondary">{t('queue.rejectReason')}</Text>
          <Input.TextArea
            rows={4}
            value={rejectNote}
            onChange={(e) => setRejectNote(e.target.value)}
            placeholder={t('queue.rejectPlaceholder')}
            maxLength={500}
          />
        </Space>
      </Modal>
    </div>
  );
};
